import { useMemo, useState } from 'react';
import { Tag } from 'lucide-react';
import { useCouponsQuery } from '@/features/coupon/api/useCouponQueries';
import { CouponCard } from '@/features/coupon/components/CouponCard';
import type {
  CouponListPagination,
  CouponOffer,
} from '@/features/campaigns/api/couponApi';

const PAGE_SIZE = 9;

export function CouponPage() {
  const [page, setPage] = useState(1);
  const query = useCouponsQuery({ page, limit: PAGE_SIZE });

  const { coupons, totalItems, totalPages } = useMemo(() => {
    const raw = query.data;
    const nested = raw?.data;
    const list: CouponOffer[] = Array.isArray(nested)
      ? nested
      : nested?.coupons || [];

    const pagination: CouponListPagination | undefined =
      (!Array.isArray(nested) ? nested?.pagination : undefined) ||
      raw?.pagination;

    const server = Boolean(pagination);
    const total = server
      ? Number(pagination?.totalCount ?? pagination?.total ?? 0)
      : list.length;
    const pages = server
      ? Number(
          pagination?.totalPages ??
            pagination?.pages ??
            Math.max(1, Math.ceil(total / PAGE_SIZE)),
        )
      : Math.max(1, Math.ceil(total / PAGE_SIZE));

    const visible = server
      ? list
      : list.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return {
      coupons: visible,
      totalItems: total,
      totalPages: pages || 1,
    };
  }, [query.data, page]);

  const rangeStart = totalItems === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, totalItems);

  const goToPage = (next: number) => {
    setPage(next);
    const main = document.querySelector('main');
    if (main) main.scrollTo({ top: 0, behavior: 'smooth' });
    else window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-5">
      <header className="text-left">
        <p className="m-0 text-[11px] font-semibold tracking-[0.12em] text-[var(--dash-brand)] uppercase">
          Offers
        </p>
        <h1 className="m-0 mt-1 text-2xl font-semibold tracking-tight text-[var(--dash-ink)]">
          Coupons
        </h1>
        <p className="mt-1 mb-0 text-sm text-[var(--dash-muted)]">
          Special offers you can claim for your next campaign.
        </p>
      </header>

      {query.isPending ? (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="camp-skeleton h-[340px] rounded-2xl border border-[var(--dash-line)]"
            />
          ))}
        </div>
      ) : coupons.length > 0 ? (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
          {coupons.map((coupon, index) => (
            <CouponCard
              key={coupon._id || coupon.id || coupon.couponCode || `coupon-${index}`}
              coupon={coupon}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-[var(--dash-line)] bg-white px-4 py-20 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--dash-brand-soft)] text-[var(--dash-brand)]">
            <Tag className="h-6 w-6" />
          </div>
          <h3 className="m-0 text-lg font-semibold text-[var(--dash-ink)]">
            No coupons available
          </h3>
          <p className="mx-auto mt-1.5 mb-0 max-w-xs text-sm text-[var(--dash-muted)]">
            Check back later for new promotional offers and discounts.
          </p>
        </div>
      )}

      {!query.isPending && totalItems > 0 ? (
        <div className="flex flex-col gap-3 border-t border-[var(--dash-line)] pt-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="m-0 text-xs text-[var(--dash-muted)]">
            {rangeStart}–{rangeEnd} of {totalItems} coupons
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => goToPage(page - 1)}
              className="rounded-xl border border-[var(--dash-line)] bg-white px-3 py-2 text-xs font-semibold text-[var(--dash-ink)] disabled:opacity-40"
            >
              Previous
            </button>
            <span className="text-xs font-semibold text-[var(--dash-muted)]">
              {page} / {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => goToPage(page + 1)}
              className="rounded-xl border border-[var(--dash-line)] bg-white px-3 py-2 text-xs font-semibold text-[var(--dash-ink)] disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
